import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
}

export interface Subscription {
  workspace_id: string;
  tier: "free" | "pro" | "team";
  status: string;
  seats: number;
  current_period_end: string | null;
}

const ACTIVE_KEY = "samebrain.activeWorkspaceId";

export function useWorkspaces() {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [subscriptions, setSubscriptions] = useState<Record<string, Subscription>>({});
  const [activeId, setActiveIdState] = useState<string | null>(localStorage.getItem(ACTIVE_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setWorkspaces([]);
      setSubscriptions({});
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      const { data: members } = await supabase
        .from("workspace_members")
        .select("workspace_id, workspaces(*)")
        .eq("user_id", user.id);
      const ws: Workspace[] = (members ?? [])
        .map((m: any) => m.workspaces)
        .filter(Boolean);
      setWorkspaces(ws);
      if (ws.length) {
        const ids = ws.map((w) => w.id);
        const { data: subs } = await supabase
          .from("subscriptions")
          .select("*")
          .in("workspace_id", ids);
        const map: Record<string, Subscription> = {};
        (subs ?? []).forEach((s: any) => (map[s.workspace_id] = s));
        setSubscriptions(map);
        if (!activeId || !ids.includes(activeId)) {
          setActiveIdState(ws[0].id);
          localStorage.setItem(ACTIVE_KEY, ws[0].id);
        }
      }
      setLoading(false);
    })();
  }, [user?.id]);

  const setActive = (id: string) => {
    setActiveIdState(id);
    localStorage.setItem(ACTIVE_KEY, id);
  };

  const active = workspaces.find((w) => w.id === activeId) ?? null;
  const activeSubscription = active ? subscriptions[active.id] ?? null : null;

  return { workspaces, subscriptions, active, activeSubscription, setActive, loading };
}
